/**
 * Confetti — celebration particle effect
 * 50 CSS particles using Framer Motion, 6 kid palette colors, 3 shapes
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { kidConfettiColors } from '../../styles/kidPalette';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface ConfettiProps {
  trigger: boolean;
  duration?: number;
  count?: number;
}

const COLORS = kidConfettiColors;
const SHAPES = ['circle', 'square', 'triangle'] as const;

interface Particle {
  id: number;
  x: number;
  color: string;
  shape: typeof SHAPES[number];
  delay: number;
  rotation: number;
  size: number;
}

function ParticleShape({ shape, color, size }: { shape: string; color: string; size: number }) {
  if (shape === 'circle') {
    return (
      <div
        className="rounded-full"
        style={{ width: size, height: size, backgroundColor: color }}
      />
    );
  }
  if (shape === 'triangle') {
    return (
      <div
        style={{
          width: 0,
          height: 0,
          borderLeft: `${size / 2}px solid transparent`,
          borderRight: `${size / 2}px solid transparent`,
          borderBottom: `${size}px solid ${color}`,
        }}
      />
    );
  }
  // square
  return (
    <div
      className="rounded-sm"
      style={{ width: size, height: size, backgroundColor: color }}
    />
  );
}

export default function Confetti({ trigger, duration = 3000, count = 50 }: ConfettiProps) {
  const [particles, setParticles] = useState<Particle[]>([]);
  const reduced = useReducedMotion();
  const [flash, setFlash] = useState(false);

  // Reduced-motion: brief golden flash instead of particles
  useEffect(() => {
    if (trigger && reduced) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 500);
      return () => clearTimeout(timer);
    }
  }, [trigger, reduced]);

  useEffect(() => {
    if (trigger && !reduced) {
      const newParticles: Particle[] = Array.from({ length: count }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
        delay: Math.random() * 0.5,
        rotation: Math.random() * 720 - 360,
        size: Math.random() * 8 + 6,
      }));
      setParticles(newParticles);

      const timer = setTimeout(() => setParticles([]), duration);
      return () => clearTimeout(timer);
    } else {
      setParticles([]);
    }
  }, [trigger, duration, count]);

  if (reduced) {
    return (
      <AnimatePresence>
        {flash && (
          <motion.div
            className="fixed inset-0 pointer-events-none z-50"
            style={{ backgroundColor: '#FFD43B' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.3, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          />
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {particles.map((p) => (
            <motion.div
              key={p.id}
              className="absolute"
              style={{ left: `${p.x}%` }}
              initial={{ y: '-5vh', rotate: 0, opacity: 1 }}
              animate={{
                y: '105vh',
                rotate: p.rotation,
                opacity: [1, 1, 0],
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: duration / 1000,
                delay: p.delay,
                ease: 'easeIn',
              }}
            >
              <ParticleShape shape={p.shape} color={p.color} size={p.size} />
            </motion.div>
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}
